import React from 'react';
import {
  CheckboxField,
  InputBirthdateField,
  InputField,
  InputMaskField,
  RadioField,
  SegmentedControlField,
  SelectField,
} from '@qover/molecules';
import messages from './form.messages';
import { pattern, required } from '@qover/utils/src/validation';

export type FieldProps = {
  'data-test'?: string;
  prefix?: string;
  options?: React.OptionHTMLAttributes<any>[];
  placeholder?: string;
};

export const FirstNameField: React.FC<FieldProps> = props => (
  <InputField
    autoComplete="given-name"
    data-test={`${props['data-test']}firstName`}
    id={`${props.prefix}firstName`}
    label={messages.firstName}
    name={`${props.prefix}firstName`}
  />
);

export const LastNameField: React.FC<FieldProps> = props => (
  <InputField
    autoComplete="family-name"
    data-test={`${props['data-test']}lastName`}
    id={`${props.prefix}lastName`}
    label={messages.lastName}
    name={`${props.prefix}lastName`}
  />
);

export const BirthdateField: React.FC<FieldProps> = props => (
  <InputBirthdateField
    autoComplete="on"
    data-test={`${props['data-test']}birthdate`}
    id={`${props.prefix}birthdate`}
    label={messages.birthdate}
    name={`${props.prefix}birthdate`}
  />
);

export const BirthplaceField: React.FC<FieldProps> = props => (
  <InputField
    data-test={`${props['data-test']}birthplace`}
    id={`${props.prefix}birthplace`}
    label={messages.birthplace}
    name={`${props.prefix}birthplace`}
    validate={required}
  />
);

export const TitleField: React.FC<FieldProps> = props => (
  <SegmentedControlField
    data-test={`${props['data-test']}title`}
    items={[
      {
        'data-test': `${props['data-test']}title.male`,
        id: `${props.prefix}title_1`,
        label: messages.male,
        value: 'Mr',
      },
      {
        'data-test': `${props['data-test']}title.female`,
        id: `${props.prefix}title_2`,
        label: messages.female,
        value: 'Mrs',
      },
    ]}
    label={messages.gender}
    name={`${props.prefix}title`}
  />
);

export const MakeField: React.FC<FieldProps> = props => (
  <SelectField
    data-test={`${props['data-test']}make`}
    id={`${props.prefix}make`}
    label={messages.make}
    name={`${props.prefix}make`}
    options={props.options}
  />
);

export const ModelField: React.FC<FieldProps> = props => (
  <InputField
    data-test={`${props['data-test']}model`}
    description={messages.example}
    id={`${props.prefix}model`}
    label={messages.model}
    name={`${props.prefix}model`}
  />
);

export const PlateNumberField: React.FC<FieldProps> = props => (
  <InputField
    data-test={`${props['data-test']}plateNumber`}
    id={`${props.prefix}plateNumber`}
    label={messages.plateNumber}
    name={`${props.prefix}plateNumber`}
  />
);

export const OwnerField: React.FC<FieldProps> = props => (
  <RadioField
    data-test={`${props['data-test']}owner`}
    id={`${props.prefix}clientType1`}
    name={`${props.prefix}status`}
    value="owner"
  >
    {messages.clientType1}
  </RadioField>
);

export const RepresentativeField: React.FC<FieldProps> = props => (
  <RadioField
    data-test={`${props['data-test']}representative`}
    id={`${props.prefix}clientType2`}
    name={`${props.prefix}status`}
    value="representative"
  >
    {messages.clientType2}
  </RadioField>
);

export const VatBeField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={['B', 'E', '0', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
    name={`${props.prefix}vatIn`}
    placeholder="BE0123456789"
    validate={pattern(/^(BE)?0[0-9]{9}$/)}
  />
);

export const VatPtField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={['P', 'T', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
    name={`${props.prefix}vatIn`}
    placeholder="PT123456789"
    validate={pattern(/^(PT)?[0-9]{9}$/)}
  />
);

export const VatPlField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={[
      'P',
      'L',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ]}
    name={`${props.prefix}vatIn`}
    placeholder="PL0123456789"
    validate={pattern(/^(PL)?[0-9]{10}$/)}
  />
);

export const VatAtField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={['A', 'T', 'U', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
    name={`${props.prefix}vatIn`}
    placeholder="ATU12345678"
    validate={pattern(/^(AT)?U[0-9]{8}$/)}
  />
);

export const VatIeField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={['I', 'E', /\d/, 'S', /\d/, /\d/, /\d/, /\d/, /\d/, 'L']}
    name={`${props.prefix}vatIn`}
    placeholder="IE1S12345L"
    validate={pattern(/^(IE)?[0-9]S[0-9]{5}L$/)}
  />
);

export const VatItField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={[
      'I',
      'T',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ]}
    name={`${props.prefix}vatIn`}
    placeholder="IT01234567891"
    validate={pattern(/^(IT)?[0-9]{11}$/)}
  />
);

export const VatEsField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={[
      'E',
      'S',
      /[0-9A-Z]/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /[0-9A-Z]/,
    ]}
    name={`${props.prefix}vatIn`}
    placeholder="ES112345678"
    validate={pattern(/^(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]$/)}
  />
);

export const VatFrField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    guide={false}
    label={messages.vat}
    mask={[
      'F',
      'R',
      /[\da-hj-np-zA-HJ-NP-Z]/,
      /[\da-hj-np-zA-HJ-NP-Z]/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ]}
    name={`${props.prefix}vatIn`}
    placeholder="FR327489399"
    validate={pattern(/^(FR)?[0-9A-Z]{2}[0-9]{9}$/i)}
    format={(val: string) => val?.toUpperCase()}
  />
);

export const VatNlField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={[
      'N',
      'L',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      'B',
      /\d/,
      /\d/,
    ]}
    name={`${props.prefix}vatIn`}
    placeholder="NL000099998B57"
    validate={pattern(/^NL[0-9]{9}B[0-9]{2}$/)}
  />
);

export const VatDeField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={['D', 'E', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
    name={`${props.prefix}vatIn`}
    placeholder="DE347593845"
    validate={pattern(/^DE[0-9]{9}/)}
  />
);

const GB_MASK = (rawInput: string) => {
  const premask = ['G', 'B', /[a-zA-Z0-9]/];
  let mask = premask;

  if (/[aA-zZ]/.test(rawInput[2])) {
    mask = [...premask, /[aA-zZ]/, /\d/, /\d/, /\d/];
  } else if (/[0-9]/.test(rawInput[2])) {
    mask = [...premask, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
  }

  if (/[0-9]/.test(rawInput[11])) {
    mask = [
      ...premask,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ];
  }

  return mask;
};

export const VatGbField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}vatIn`}
    id={`${props.prefix}vatIn`}
    label={messages.vat}
    mask={GB_MASK}
    name={`${props.prefix}vatIn`}
    placeholder="GB999999999"
    validate={pattern(/(GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})$/)}
  />
);

export const CompanyNameField: React.FC<FieldProps> = props => (
  <InputField
    autoComplete="organization"
    data-test={`${props['data-test']}companyName`}
    id={`${props.prefix}companyName`}
    label={messages.companyName}
    name={`${props.prefix}companyName`}
  />
);

export const BillingAddressSameAsCompanyField: React.FC<FieldProps> = props => (
  <CheckboxField
    data-test={`${props['data-test']}billingAddressSameAsCompany`}
    id={`${props.prefix}sameAsLevalAddress`}
    name={`${props.prefix}billingAddressSameAsCompany`}
    optional
  >
    {messages.sameAsLevalAddress}
  </CheckboxField>
);

export const LanguageField: React.FC<FieldProps> = props => (
  <SelectField
    data-test={`${props['data-test']}language`}
    id={`${props.prefix}language`}
    label={messages.language}
    name={`${props.prefix}language`}
    options={props.options}
    required
  />
);

export const PhoneField: React.FC<FieldProps> = props => (
  <InputField
    data-test={`${props['data-test']}phone`}
    id={`${props.prefix}phone`}
    label={messages.phone}
    name={`${props.prefix}phone`}
    placeholder={props.placeholder}
  />
);

export const EmailField: React.FC<FieldProps> = props => (
  <InputField
    autoComplete="email"
    type="email"
    data-test={`${props['data-test']}email`}
    id={`${props.prefix}email`}
    label={messages.email}
    name={`${props.prefix}email`}
  />
);

export const SsnPtField: React.FC<FieldProps> = props => (
  <InputMaskField
    data-test={`${props['data-test']}nationalRegisterNumber`}
    id={`${props.prefix}nationalRegisterNumber`}
    label={messages.nationalRegisterNumber}
    mask={[/[12359]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
    name={`${props.prefix}nationalRegisterNumber`}
    validate={pattern(/^[12359][0-9]{8}$/)}
  />
);

export const SsnEsField: React.FC<FieldProps> = props => (
  <InputField
    data-test={`${props['data-test']}nationalRegisterNumber`}
    id={`${props.prefix}nationalRegisterNumber`}
    label={messages.nationalRegisterNumber}
    name={`${props.prefix}nationalRegisterNumber`}
    validate={required}
  />
);
